import React, { useState } from 'react';
import { Alert, Button, Checkbox, Collapse, Input, Space, Tag } from 'antd';
import {
  errorMessage,
  fetchNewsCuration,
  resolveTag,
  updateNewsCuration,
} from '../../MarketExplorer/api';
import { NewsCuration } from '../../MarketExplorer/types';
import { ProposalItem } from './types';

const ProposalCard: React.FC<{ item: ProposalItem }> = ({ item }) => {
  const [source, setSource] = useState(item.source);
  const [headline, setHeadline] = useState(item.proposal.headline || '');
  const [useHeadline, setUseHeadline] = useState(
    Boolean(item.proposal.headline),
  );
  const [tags, setTags] = useState<string[]>([]);
  const [instruments, setInstruments] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [latest, setLatest] = useState<NewsCuration | null>(null);
  const validProposal =
    !item.proposal.stale &&
    ['accepted', 'needs_review'].includes(item.proposal.status);
  const canSave =
    validProposal &&
    ((useHeadline && headline.trim() && headline.trim() !== source.headline) ||
      tags.length > 0 ||
      instruments.length > 0);
  const save = async () => {
    if (saving || !canSave || latest) return;
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const current = await fetchNewsCuration(source.id);
      if (current.revision !== source.revision) {
        setLatest(current);
        setError(
          'This article changed after the proposal was prepared. Review the current article before applying any suggestion.',
        );
        return;
      }
      const resolved = [];
      for (const text of tags) resolved.push(await resolveTag(text));
      for (const id of instruments) resolved.push(await resolveTag('', id));
      const changed = await updateNewsCuration(source.id, {
        expected_revision: source.revision,
        ...(useHeadline ? { headline: headline.trim() } : {}),
        ...(resolved.length
          ? {
              add_tag_ids: Array.from(
                new Set(resolved.map(tag => tag.canonical_tag_id || tag.id)),
              ),
            }
          : {}),
      });
      setSource(changed);
      setTags([]);
      setInstruments([]);
      setSaved(true);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setSaving(false);
    }
  };
  const proposal = item.proposal;
  return (
    <article className="corpus-news__proposal">
      {item.proposal.stale && (
        <Alert
          type="warning"
          showIcon
          title="This proposal uses an older source. Prepare the article again before applying it."
        />
      )}
      <Space wrap>
        <Tag
          color={
            proposal.status === 'failed'
              ? 'error'
              : proposal.status === 'needs_review'
                ? 'warning'
                : 'default'
          }
        >
          Model: {proposal.status.replace(/_/g, ' ')}
        </Tag>
        <span className="corpus-news__muted">PM news #{item.news_id}</span>
      </Space>
      <h3>{source.headline}</h3>
      <p className="corpus-news__muted">
        {proposal.reason || 'Review the source and suggestion before saving.'}
      </p>
      <Collapse
        ghost
        items={[
          {
            key: 'source',
            label: 'Read original article',
            children: (
              <div className="corpus-news__article-body">{source.body}</div>
            ),
          },
        ]}
      />
      {(Boolean(proposal.removed_clues?.length) ||
        Boolean(proposal.support_quotes?.length) ||
        proposal.residual_recognizability) && (
        <Collapse
          ghost
          items={[
            {
              key: 'rationale',
              label: 'Review supporting text and removed clues',
              children: (
                <div>
                  {proposal.support_quotes?.map((quote, index) => (
                    <blockquote key={`quote-${index}`}>{quote}</blockquote>
                  ))}
                  {proposal.removed_clues?.map((clue, index) => (
                    <p key={`clue-${index}`}>Removed clue: {clue}</p>
                  ))}
                  {proposal.residual_recognizability && (
                    <p>
                      Remaining recognizability:{' '}
                      {proposal.residual_recognizability}
                    </p>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}
      {proposal.headline && (
        <div className="corpus-news__suggestion">
          <Checkbox
            checked={useHeadline}
            disabled={saving || !validProposal}
            onChange={event => setUseHeadline(event.target.checked)}
          >
            Use improved headline
          </Checkbox>
          <Input.TextArea
            aria-label={`Proposed headline for news ${item.news_id}`}
            value={headline}
            onChange={event => setHeadline(event.target.value)}
            autoSize={{ minRows: 2, maxRows: 5 }}
            disabled={saving || !validProposal}
          />
        </div>
      )}
      {Boolean(proposal.suggested_tags?.length) && (
        <div className="corpus-news__suggestion">
          <strong>Suggested tags</strong>
          <Checkbox.Group
            value={tags}
            onChange={values => setTags(values as string[])}
            options={proposal.suggested_tags}
            disabled={saving || !validProposal}
          />
        </div>
      )}
      {Boolean(proposal.instrument_ids?.length) && (
        <div className="corpus-news__suggestion">
          <strong>Instrument tags</strong>
          <Checkbox.Group
            value={instruments}
            onChange={values => setInstruments(values as number[])}
            options={proposal.instrument_ids?.map(id => ({
              value: id,
              label:
                proposal.instruments?.find(value => value.id === id)?.ric ||
                proposal.instruments?.find(value => value.id === id)?.name ||
                `PM instrument #${id}`,
            }))}
            disabled={saving || !validProposal}
          />
        </div>
      )}
      {!!proposal.errors?.length && (
        <Alert
          type="warning"
          showIcon
          title="Preparation needs attention"
          description={proposal.errors.map(errorMessage).join(' ')}
        />
      )}
      {error && <Alert type="error" showIcon title={error} />}
      {latest && (
        <div className="corpus-news__conflict">
          <strong>Current headline</strong>
          <p>{latest.headline}</p>
          <div className="corpus-news__article-body">{latest.body}</div>
          <Button
            onClick={() => {
              setSource(latest);
              setLatest(null);
              setError('');
            }}
          >
            I reviewed the current article
          </Button>
        </div>
      )}
      {saved && (
        <Alert type="success" showIcon title="Selected changes saved to PM." />
      )}
      <Button
        type="primary"
        loading={saving}
        disabled={!canSave || Boolean(latest)}
        onClick={save}
      >
        Save selected changes
      </Button>
    </article>
  );
};
const ProposalReview: React.FC<{ items: ProposalItem[]; jobId: string }> = ({
  items,
  jobId,
}) => (
  <section aria-label="Review news proposals">
    <p className="corpus-news__muted">
      These are model suggestions, not approved edits. Saving updates the
      original PM headline and only the tags you select; the source body stays
      intact.
    </p>
    <Collapse
      items={items.map(item => ({
        key: `${jobId}:${item.news_id}`,
        label: item.source.headline,
        extra: (
          <Tag color={item.proposal.status === 'failed' ? 'error' : undefined}>
            {item.proposal.status.replace(/_/g, ' ')}
          </Tag>
        ),
        children: <ProposalCard item={item} />,
      }))}
    />
  </section>
);
export default ProposalReview;
